import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Avatar from './Avatar';

// Check if running in Electron
const isElectron = Boolean(window.electronAPI);

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleWindowControl = useCallback(async (action: 'minimize' | 'maximize' | 'close') => {
    const api = window.electronAPI;
    if (!api) {
      return;
    }

    try {
      if (action === 'minimize') {
        await api.minimize();
      } else if (action === 'maximize') {
        await api.maximize();
      } else if (action === 'close') {
        await api.close();
      }
    } catch (error) {
      console.error(`Failed to ${action} window`, error);
    }
  }, []);

  return (
    <>
      <nav
        className="bg-gradient-to-r from-white via-indigo-50 to-purple-50 dark:bg-gradient-to-r dark:from-gray-900 dark:to-gray-800 py-2 px-4 shadow-xl border-b-2 border-indigo-100 dark:border-gray-700"
        style={isElectron ? { WebkitAppRegion: 'drag' } as any : undefined}
      >
        <div
          className="container mx-auto flex items-center justify-between gap-4"
          style={isElectron ? { WebkitAppRegion: 'no-drag' } as any : undefined}
        >
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"
              onClick={handleNavClick}
            >
              ServeRS
            </Link>

            {/* Desktop Navigation */}
            {isAuthenticated && (
              <ul className="hidden items-center gap-6 text-sm font-medium md:flex">
                <li>
                  <Link
                    to="/"
                    className={`${isActive('/') ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'} transition-all duration-300 py-1`}
                  >
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/library"
                    className={`${isActive('/library') ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'} transition-all duration-300 py-1`}
                  >
                    {t('nav.library')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/profile"
                    className={`${isActive('/profile') ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'} transition-all duration-300 py-1`}
                  >
                    {t('nav.profile')}
                  </Link>
                </li>
                {user?.role === 'admin' && (
                  <li>
                    <Link
                      to="/admin"
                      className={`${isActive('/admin') || location.pathname.startsWith('/admin') ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-300 dark:text-gray-300 light:text-gray-700 hover:text-yellow-400'} transition-all duration-300 py-1 flex items-center gap-1`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {t('nav.admin')}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 md:gap-2 md:mr-2">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 rounded text-xs font-bold transition-colors ${language === 'en' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-200 dark:bg-gray-800'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('tr')}
                className={`px-2 py-1 rounded text-xs font-bold transition-colors ${language === 'tr' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-200 dark:bg-gray-800'}`}
              >
                TR
              </button>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <div className="flex items-center gap-4 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 shadow-md dark:shadow-none">
                  <div className="flex items-center gap-3">
                    <Avatar src={user?.avatarUrl} alt={user?.displayName || user?.username} size="sm" />
                    <span className="text-gray-700 dark:text-gray-200 font-medium">
                      {user?.displayName || user?.username}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="rounded-full bg-gradient-to-r from-red-600 to-red-700 px-4 py-2 text-sm font-semibold text-white shadow-lg transition duration-300 hover:from-red-700 hover:to-red-800 hover:scale-105"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                location.pathname !== '/login' && (
                  <div className="flex items-center gap-3">
                    <Link
                      to="/login"
                      className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-2 font-semibold text-white shadow-lg transition duration-300 hover:from-blue-700 hover:to-purple-700 hover:scale-105"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/login?register=true"
                      className="rounded-full bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-2 font-semibold text-white shadow-lg transition duration-300 hover:from-green-700 hover:to-emerald-700 hover:scale-105"
                    >
                      {t('nav.register')}
                    </Link>
                  </div>
                )
              )}
            </div>

            {/* Mobile Menu Button (only on web, not in Electron) */}
            {!isElectron && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            )}

            {/* Electron window controls - only show in Electron */}
            {isElectron && (
              <div className="flex items-center gap-1 ml-4" style={{ WebkitAppRegion: 'no-drag' } as any}>
                <button
                  type="button"
                  onClick={() => handleWindowControl('minimize')}
                  className="flex h-8 w-10 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-700 hover:text-white"
                  aria-label="Minimize window"
                >
                  <svg className="h-3 w-3" viewBox="0 0 10 1" fill="currentColor" aria-hidden="true">
                    <rect width="10" height="1" rx="0.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleWindowControl('maximize')}
                  className="flex h-8 w-10 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-700 hover:text-white"
                  aria-label="Maximize window"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
                    <rect x="0.5" y="0.5" width="10" height="10" rx="1" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleWindowControl('close')}
                  className="flex h-8 w-10 items-center justify-center rounded-md text-gray-400 transition hover:bg-red-600 hover:text-white"
                  aria-label="Close window"
                >
                  <svg className="h-4 w-4" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
                    <path d="M1 1l8 8M9 1L1 9" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {!isElectron && isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm">
          <div className="flex flex-col h-full pt-16 px-6 pb-6">
            {/* Close button */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Mobile Navigation Links */}
            <nav className="flex-1 space-y-4">
              <Link
                to="/"
                onClick={handleNavClick}
                className={`block text-xl font-medium py-3 px-4 rounded-lg transition-colors ${isActive('/') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
              >
                {t('nav.home')}
              </Link>

              {isAuthenticated && (
                <>
                  <Link
                    to="/library"
                    onClick={handleNavClick}
                    className={`block text-xl font-medium py-3 px-4 rounded-lg transition-colors ${isActive('/library') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                  >
                    {t('nav.library')}
                  </Link>
                  <Link
                    to="/profile"
                    onClick={handleNavClick}
                    className={`block text-xl font-medium py-3 px-4 rounded-lg transition-colors ${isActive('/profile') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                  >
                    {t('nav.profile')}
                  </Link>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={handleNavClick}
                      className={`block text-xl font-medium py-3 px-4 rounded-lg transition-colors ${isActive('/admin') || location.pathname.startsWith('/admin') ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                    >
                      {t('nav.admin')}
                    </Link>
                  )}
                </>
              )}
            </nav>

            {/* Mobile Auth Section */}
            <div className="border-t border-gray-700 pt-6 space-y-4">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-800 rounded-lg">
                    <Avatar src={user?.avatarUrl} alt={user?.displayName || user?.username} size="md" />
                    <span className="text-gray-200 font-medium text-lg">
                      {user?.displayName || user?.username}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full rounded-lg bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 text-lg font-semibold text-white transition hover:from-red-700 hover:to-red-800"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    onClick={handleNavClick}
                    className="block w-full text-center rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 text-lg font-semibold text-white transition hover:from-blue-700 hover:to-purple-700"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/login?register=true"
                    onClick={handleNavClick}
                    className="block w-full text-center rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-lg font-semibold text-white transition hover:from-green-700 hover:to-emerald-700"
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;